import { Component, OnInit, OnDestroy, Input, Output, EventEmitter, ViewChild, ElementRef } from '@angular/core';
import { DataService } from '../../services/data.service';
import { StoreService } from '../../services/store.service';
import { ToolsService } from '../../services/tools.service';
import { StripePaymentService } from '../../services/stripe-payment.service';
import { pages } from '../../alv-enums';
import { Receipt } from '../../models/models';
import * as tools from '../../tools';
import { paymentMode } from 'src/app/models/payment';
import { HttpHeaders } from '@angular/common/http';
import { Utility } from 'src/app/common/utility';
import { ReCaptchaV3Service, RecaptchaComponent } from 'ng-recaptcha';
import { Router } from '@angular/router';

@Component({
	selector: 'payment',
	templateUrl: './payment.component.html',
	styleUrls: ['./payment.component.scss']
})
export class PaymentComponent implements OnInit, OnDestroy {

	@ViewChild('captchaRef') captchaRef!: RecaptchaComponent;
	@ViewChild('magSwipeInput') magSwipeField: ElementRef;

	@Input() public currentPage: pages;
	@Input() public registerId: number;
	@Input() public kioskId: number;
	captcha: boolean = false;
	public paymentInputMode: boolean = false;
	public paymentComplete = false;
	public paymentInProgress = false;
	public isSubmittingCardPayment: boolean = false;

	@Output() private pageChange = new EventEmitter<pages>();

	public swipeActive = false;
	public magSwipe: string;
	public cardNumber: string;
	public monthExp: string;
	public yearExp: string;
	public nameOnCard: string;
	public cvvCode: string;
	public paymentMode: paymentMode;

	public pagesEnum: any;
	public paymentModeEnum: any;

	public terminalStatusMessage: string = '';
	public terminalError: string = '';
	private paymentDoneTimer: any = null;

	constructor(
		private dataService: DataService,
		private toolsService: ToolsService,
		public storeService: StoreService,
		protected recaptchaV3Service: ReCaptchaV3Service,
		protected router: Router,
		private stripePaymentService: StripePaymentService
	) {
		this.pagesEnum = pages;
		this.paymentModeEnum = paymentMode;
	}

	ngOnInit() {
		this.paymentInputMode = !this.storeService.kioskSettings.enableCreditSwipe;
	}

	ngOnDestroy() {
		this.cancelTerminalIfActive();
		if (this.paymentDoneTimer) {
			clearTimeout(this.paymentDoneTimer);
		}
	}

	private cancelTerminalIfActive() {
		if (this.paymentMode === paymentMode.StripeTerminal && this.paymentInProgress) {
			this.stripePaymentService.cancelKioskPayment(
				this.registerId, this.storeService.kioskSettings.locationId)
				.subscribe(() => {}, () => {});
		}
	}

	goToPage(page: pages) {
		this.pageChange.emit(page);
		window.scroll(0, 0)
	}

	paymentByCard() {
		const settings = this.storeService.kioskSettings;

		if (settings.gatewayProcessor === 'Stripe') {
			if (settings.enableCreditSwipe && settings.enableStripeTerminalReader) {
				this.swipeActive = true;
				this.paymentMode = paymentMode.StripeTerminal;
				this.processKioskTerminalPayment();
			} else {
				this.swipeActive = true;
				this.paymentMode = paymentMode.CreditCard;
				this.paymentInputMode = true;
			}
		} else {
			this.swipeActive = true;
			this.paymentMode = paymentMode.CreditCard;

			setTimeout(() => {
				if (this.magSwipeField != undefined) {
					this.magSwipeField.nativeElement.focus();
				}
			}, 250);
		}
	}

	paymentByCash() {
		this.swipeActive = true;
		this.paymentMode = paymentMode.Cash;
	}

	paymentByStripeTerminal() {
		this.swipeActive = true;
		this.paymentMode = paymentMode.StripeTerminal;
		this.processKioskTerminalPayment();
	}

	async processKioskTerminalPayment() {
		if (this.paymentInProgress) return;

		const cart = this.storeService.shoppingCart;
		const settings = this.storeService.kioskSettings;
		this.paymentInProgress = true;
		this.terminalError = '';
		this.terminalStatusMessage = 'Finish payment on reader...';

		try {
			const result = await this.stripePaymentService.processKioskTerminalPayment({
				registerId: this.registerId,
				locationId: settings.locationId,
				transactionId: cart.transactionId,
				guestId: cart.guestId,
				guestName: cart.guestName || '',
				guestCallName: cart.guestName || '',
				paymentAmount: cart.total,
				paymentSourceTypeId: 1017
			}).toPromise();

			this.paymentInProgress = false;
			if (result.requiresManualEntry) {
				this.fallbackToHandType(result.message || 'Switching to manual entry.');
			} else if (result.isSuccess) {
				this.toolsService.showSuccess('Payment completed successfully!');
				this.paymentDone();
			} else {
				this.handleTerminalError(result.message || 'Payment failed.');
			}
		} catch (error) {
			this.paymentInProgress = false;
			this.fallbackToHandType('Switching to manual card entry.');
		}
	}

	private handleTerminalError(message: string) {
		this.paymentInProgress = false;
		this.terminalError = message;
		this.terminalStatusMessage = '';
	}

	switchToManualEntry() {
		this.cancelTerminalIfActive();
		this.fallbackToHandType('Switching to manual card entry.');
	}

	private fallbackToHandType(message: string) {
		this.paymentInProgress = false;
		this.terminalStatusMessage = '';
		this.terminalError = '';
		this.paymentMode = paymentMode.CreditCard;
		this.paymentInputMode = true;
		this.toolsService.showInfo(message);
	}

	paymentCancel() {
		this.cancelTerminalIfActive();
		this.swipeActive = false;
		this.paymentInProgress = false;
		this.terminalStatusMessage = '';
		this.terminalError = '';
	}

	onMagSwipe() {
		if (!this.magSwipe || this.paymentInProgress) {
			return;
		}

		this.paymentInProgress = true;
		const postData = {
			magSwipe: this.magSwipe,
			orderId: this.storeService.shoppingCart.orderId,
			guestName: this.storeService.shoppingCart.guestName,
			guestId: this.storeService.shoppingCart.guestId,
			guestEmail: this.storeService.shoppingCart.guestEmail,
			saleAmount: this.storeService.shoppingCart.total,
			transactionId: this.storeService.shoppingCart.transactionId,
			browserOffset: tools.geBrowserTimeZoneandOffset(),
			clientUtcDateTimeStr: tools.getDateFormat_MMddyyyyhhmmss(new Date().toLocaleString()),
			timeZoneOffset: tools.getTimeZoneoffset(),
			locationId: this.storeService.kioskSettings.locationId
		};

		this.magSwipe = '';

		this.dataService.post<Receipt>(`/Checkout/ProcessCardknoxPayment`, postData)
			.then(response => {
				this.paymentInProgress = false;
				this.toolsService.ProcessHttpResponse(response, () => {
					this.storeService.shoppingCart.transactionSourceType = response.data.transactionSourceType;
					this.storeService.shoppingCart.transactionSourceTypeId = response.data.transactionSourceTypeId;
					this.storeService.shoppingCart.isTicketType = response.data.isTicketType;
					this.storeService.shoppingCart.isContainsPdf = response.data.isContainsPdf;
					this.toolsService.showSuccess(response.message);
					this.paymentDone();
				});
			})
			.catch(() => {
				this.paymentInProgress = false;
				this.toolsService.showError('Payment failed. Please try again.');
			});
	}

	resolved(token) {
		this.onCardInputSubmit(token, 2)
	}

	submitCardPayment() {
		if (!this.isSubmittingCardPayment) {
			this.isSubmittingCardPayment = true;
			this.recaptchaV3Service.execute('postguestcheckout')
				.subscribe((token) => {
					this.onCardInputSubmit(token, 3);
				}, (error) => {
					this.isSubmittingCardPayment = false;
				});
		}
	}

	onCardInputSubmit(token, tokenVersion) {
		if (this.paymentInProgress) {
			this.isSubmittingCardPayment = false;
			return;
		}

		this.paymentInProgress = true;
		this.storeService.shoppingCart.cardName = this.nameOnCard;
		this.storeService.shoppingCart.cardNumber = this.cardNumber;
		this.storeService.shoppingCart.cardExpMM = this.monthExp;
		this.storeService.shoppingCart.cardExpYY = this.yearExp;
		const httpOptions = {
			headers: new HttpHeaders({
				'Content-Type': 'application/json',
				"X-XSRF-Token": this.storeService.antiForgeryToken
			}),
			withCredentials: true
		};

		const postData = {
			orderId: this.storeService.shoppingCart.orderId,
			cardName: this.storeService.shoppingCart.cardName,
			cardNumber: this.storeService.shoppingCart.cardNumber,
			cardExpMM: this.storeService.shoppingCart.cardExpMM,
			cardExpYY: this.storeService.shoppingCart.cardExpYY,
			cardCVV: this.cvvCode,
			guestName: this.storeService.shoppingCart.guestName,
			guestEmail: this.storeService.shoppingCart.guestEmail,
			guestId: this.storeService.shoppingCart.guestId,
			saleAmount: this.storeService.shoppingCart.total,
			transactionId: this.storeService.shoppingCart.transactionId,
			browserOffset: tools.geBrowserTimeZoneandOffset(),
			clientUtcDateTimeStr: tools.getDateFormat_MMddyyyyhhmmss(new Date().toLocaleString()),
			timeZoneOffset: tools.getTimeZoneoffset(),
			locationId: this.storeService.kioskSettings.locationId,
			token: token,
			tokenVersion: tokenVersion
		};
		this.dataService.post<Receipt>(`/Checkout/ProcessCardInputPayment`, postData, null, true, false, httpOptions)
			.then(response => {
				this.paymentInProgress = false;
				this.isSubmittingCardPayment = false;
				if (response.captcha) {
					this.toolsService.ProcessHttpResponse(response, () => {
						this.storeService.shoppingCart.transactionSourceType = response.data.transactionSourceType;
						this.storeService.shoppingCart.transactionSourceTypeId = response.data.transactionSourceTypeId;
						this.storeService.shoppingCart.isTicketType = response.data.isTicketType;
						this.storeService.shoppingCart.isContainsPdf = response.data.isContainsPdf;
						this.toolsService.showSuccess(response.message);
						this.paymentDone();
					}, () => {
						this.captchaRef.reset();
						Utility.readAntiForgeryToken();
					})
				}
				else {
					if (response.attemptsExceeded >= 3) {
						this.gotToDashBoard();
					}
					this.captcha = true;
				}
			})
			.catch((error) => {
				this.storeService.antiForgeryToken = Utility.readAntiForgeryToken();
				this.paymentInProgress = false;
				this.isSubmittingCardPayment = false;
			});
	}

	paymentDone() {
		this.paymentComplete = true;
		this.paymentDoneTimer = setTimeout(() => {
			this.storeService.shoppingCart.paymentDone = true;
			if (!this.storeService.kioskSettings.isDisablePaperReceipt || this.storeService.kioskSettings.showEmail) {
				this.goToPage(pages.Receipt);
			} else {
				this.goToPage(pages.ThankYou);
			}
		}, 3000);
	}

	completeTransaction() {
		const postData = {
			transactionId: this.storeService.shoppingCart.transactionId,
			guestCallName: this.storeService.shoppingCart.guestName,
			guestEmail: this.storeService.shoppingCart.guestEmail,
			browserOffset: tools.geBrowserTimeZoneandOffset(),
			clientUtcDateTimeStr: tools.getDateFormat_MMddyyyyhhmmss(new Date().toLocaleString()),
			timeZoneOffset: tools.getTimeZoneoffset(),
		};
		this.dataService.post<Receipt>(`/Checkout/CompleteZeroTransaction`, postData)
			.then(response => {
				this.toolsService.ProcessHttpResponse(response, () => {
					if (response.succeeded) {
						this.storeService.shoppingCart.paymentDone = true;
						this.goToPage(pages.Receipt);
					}
				});
			})
			.catch(() => {
				this.toolsService.showError('Failed to complete transaction. Please try again.');
			});
	}

	saveTransaction() {
		const fees = this.storeService.shoppingCart.transactionFees;
		const postData = {
			transactionId: this.storeService.shoppingCart.transactionId,
			guestCallName: this.storeService.shoppingCart.guestName,
			guestEmail: this.storeService.shoppingCart.guestEmail,
			isAuthOnly: false,
			transactionFee: fees ? fees.transactionFee : 0,
			transactionFeeTaxAmount: fees ? fees.transactionFeeTaxAmount : 0,
		};
		this.dataService.post<Receipt>(`/Checkout/SaveUserTransaction`, postData)
			.then(response => {
				this.toolsService.ProcessHttpResponse(response, () => {
					if (response.succeeded) {
						this.storeService.shoppingCart.paymentDone = true;
						this.goToPage(pages.Receipt);
					}
				});
			})
			.catch(() => {
				this.toolsService.showError('Failed to save transaction. Please try again.');
			});
	}

	goBack() {
		if (this.storeService.kioskSettings.waiverRequired) {
			this.goToPage(pages.Waiver)
		}
		else {
			if (this.storeService.kioskSettings.hideInfoStep) {
				this.goToPage(pages.Order)
			}
			else {
				this.goToPage(pages.CustomerInfo);
			}
		}
		window.scroll(0, 0);
	}

	onMagSwipeBlur() {
		if (this.magSwipeField) {
			this.magSwipeField.nativeElement.focus();
		}
	}

	gotToDashBoard() {
		if (this.registerId) {
			if (this.kioskId) {
				this.router.navigate([`/dashboard/${this.registerId}/${this.kioskId}`]);
			} else {
				this.router.navigate([`/dashboard/${this.registerId}`]);
			}
		}
	}
	cardOptionChange($event)
	{
		if ($event === 'back') {
			this.swipeActive = false;
			this.paymentInputMode = false;
		} else {
			this.paymentInputMode = $event === 'keyin';
		}
	}

}
